import { Injectable, NgZone } from '@angular/core';
import { IonicErrorHandler, App } from 'ionic-angular';
import { HttpErrorResponse } from '@angular/common/http';
import { BugsnagProvider } from './bugsnag';
import { OnPremiseProvider } from './on-premise';
import { API } from './api';
import { ErrorCallbackProvider } from './error-callback';
import { LogoutProvider } from './logout';
import { BrainBankErrorCode } from './error-code';
import { ModalProvider } from './popup/modal';
import { PriorityLevel } from './popup/base';
import { SupportProvider } from './support';
import { TranslateService } from '@ngx-translate/core';

export interface ErrorCodeObject {
    title: string;
    message: string;
    buttonText?: string;
    customCSS?: string;
}

export interface ErrorCodeMap {
    [key: string]: ErrorCodeObject;
}

@Injectable()
export class NeuralaErrorHandler extends IonicErrorHandler {
    private modals = {}; // Prevents multiple instances of the same modal
    private errorCodeObjectsMap: ErrorCodeMap;

    constructor(
        private modalProvider: ModalProvider,
        private errorCallbackProvider: ErrorCallbackProvider,
        private zone: NgZone,
        private bugsnag: BugsnagProvider,
        private api: API,
        private appCtrl: App,
        private logoutProvider: LogoutProvider,
        private onPremiseProvider: OnPremiseProvider,
        private supportProvider: SupportProvider,
        private translate: TranslateService
    ) {
        super();

        this.translate.onLangChange.subscribe(() => {
            this.initializeErrorCodeMap(true);
        });
    }

    private initializeErrorCodeMap(force = false): void {
        // https://neurala.atlassian.net/browse/BB-5929
        // Lazy-load error messages to allow translation init to occur first
        if (!this.errorCodeObjectsMap || force) {
            this.errorCodeObjectsMap = this.getErrorCodeMap();
        }
    }

    private getErrorCodeMap(): ErrorCodeMap {
        return {
            CANNOT_RETAG_IMAGE: {
                title: this.translate.instant('Cannot change ground truth'),
                message: this.translate.instant(
                    'Once you have uploaded static training / validation sets, you cannot change the ground truth for images in that dataset. If you would like to change the ground truth for this image, you must delete it and reupload it as the new class.'
                )
            },
            PROHIBITED_CHARACTERS: {
                title: this.translate.instant('Invalid characters'),
                message: `${this.translate.instant(
                    'Class names are restricted to characters'
                )}: <span>A-Z  0-9  -  _</span>`,
                buttonText: this.translate.instant('OK'),
                customCSS: 'prohibited-characters'
            }
        };
    }

    async handleError(err: any): Promise<void> {
        this.initializeErrorCodeMap();

        const finalError = err.rejection || err;
        if (await this.errorCallbackProvider.shouldIgnoreError(finalError)) {
            return;
        }

        if (!(finalError instanceof HttpErrorResponse)) {
            // Call parent handleError method if it is not an httpError
            super.handleError(err);
        } else if (finalError.status === 401) {
            if (finalError.error && finalError.error.code.includes(BrainBankErrorCode.AccountLocked)) {
                await this.handleAccountLocked();
            }
        } else if (finalError.status == 400) {
            this.zone.run(() => {
                if (finalError.error) {
                    // If we have error for which we want non-generic title and message, we can add
                    // the title and message to object map, and it will be caught and proper message will be displayed
                    const errorCodeObject = this.errorCodeObjectsMap[finalError.error.code];
                    if (errorCodeObject) {
                        this.modalProvider.presentError(
                            errorCodeObject.title,
                            errorCodeObject.message,
                            'error',
                            errorCodeObject.buttonText || this.translate.instant('Close'),
                            errorCodeObject.customCSS
                        );
                    } else {
                        this.modalProvider.presentError(
                            this.translate.instant('Something went wrong'),
                            finalError.error.message,
                            undefined,
                            this.translate.instant('OK')
                        );
                    }
                } else {
                    this.throwGenericError();
                }
            });
        } else if (finalError.error && finalError.error.code == BrainBankErrorCode.DatasetBusy) {
            this.showModalOnce('SystemBusyModal', 'system-busy');
        } else if (finalError.status == 500) {
            this.zone.run(() => this.throwGenericError());
        } else if (finalError.status == 0) {
            if (this.onPremiseProvider.isOnPremiseElectronApp()) {
                this.zone.run(() => this.navigateToExternalServiceAlertPage());
            } else {
                this.zone.run(() => this.throwGenericError());
            }
        }

        if (
            this.api.getEnvironment() == 'staging' ||
            this.api.getEnvironment() == 'production' ||
            this.api.getEnvironment() == 'releasecandidate'
        ) {
            this.bugsnag.logError(finalError, { metaData: { response: finalError.error } });
        }

        if (this.onPremiseProvider.isOnPremiseElectronApp()) {
            if (typeof finalError == 'object') {
                console.log(JSON.stringify(finalError));
            } else {
                console.log(finalError);
            }
        } else {
            console.log(finalError);
        }
    }

    showModalOnce(modalName: string, cssClass: string, callback?: Function): void {
        if (!this.modals[modalName]) {
            this.zone.run(() => {
                this.modals[modalName] = this.modalProvider.createModal(modalName, {}, { cssClass });
                this.modals[modalName].onDidDismiss(async () => {
                    if (callback) {
                        await callback();
                    }

                    this.modals[modalName] = null;
                });
                this.modals[modalName].present();
            });
        }
    }

    private navigateToAboutPage(): void {
        setTimeout(() => {
            this.appCtrl.getActiveNav().setRoot('AccountPage', {
                subView: 'about'
            });
        }, 0);
    }

    private navigateToExternalServiceAlertPage(): void {
        setTimeout(() => {
            this.appCtrl.getActiveNav().setRoot('ExternalServiceAlertPage', {
                brainBank: false
            });
        }, 0);
    }

    private throwGenericError(): void {
        // On-premise users should have the option to generate logs; cloud users will need to email us for the time being.
        const secondaryButtons = this.onPremiseProvider.isOnPremiseElectronApp()
            ? [
                  {
                      text: this.translate.instant('Generate Logs'),
                      callback: async () => {
                          const folderPath = await this.onPremiseProvider.getDownloadPath();
                          await this.onPremiseProvider.archiveLogs(folderPath);
                          this.navigateToExternalServiceAlertPage();
                      }
                  }
              ]
            : [];

        this.modalProvider.createNotification({
            priority: PriorityLevel.Error,
            title: this.translate.instant('Something went wrong'),
            message: this.onPremiseProvider.isOnPremiseElectronApp()
                ? this.translate.instant(
                      '<p>An unknown error has occured. If this problem persists, please contact your Neurala representative for assistance or email [[email]].</p><p>You can help troubleshoot this problem by sending your VIA logs with the line “Unknown Error” in the subject line of the email. We will assess and get back to you as soon as possible.</p>',
                      { email: this.supportProvider.getSupportEmail() }
                  )
                : this.translate.instant(
                      'If the error persists, please contact support or email your account representative.'
                  ),
            primaryText: this.onPremiseProvider.isOnPremiseElectronApp()
                ? this.translate.instant('Dismiss')
                : this.translate.instant('OK'),
            secondaryButtons,
            materialIconName: 'error'
        });
    }

    public async handleAccountLocked(): Promise<void> {
        const supportEmail = this.supportProvider.getSupportEmail();
        const emailLink = `<a a href="mailto:${supportEmail}">${supportEmail}</a>`;
        const message = `<p>${this.translate.instant('Contact [[emailLink]] with questions or to unlock the account.', {
            emailLink
        })}</p>`;

        await this.modalProvider.presentAlert(
            this.translate.instant('This account is locked.'),
            message,
            this.translate.instant('OK')
        );

        await this.logoutProvider.logout();
        this.appCtrl.getActiveNavs()[0].setRoot('LoginPage');
    }
}



// WEBPACK FOOTER //
// ./src/providers/error.ts